import {registerStepsComponents} from 'constants/auth'
import {useEffect, useMemo, useState} from 'react'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {registerSchema} from 'services/utils/validationsSchemas'
import useLogin from 'logic/Auth/useLogin'
import useVerifySms from 'logic/Auth/useVerifySms'
import useSignup from 'logic/Auth/useSignup'
import {useVerifyOrgCodeMutation} from 'store/Store'
import analyticsService from 'services/analytic'

const analyticsFunctions = {
    0: {
        next: analyticsService.onboardingPressedNextConnectionDetails,
    },
    1: {
        next: analyticsService.onboardingPressedNextPhoneVerification,
        back: analyticsService.onboardingPressedBackPhoneVerification,
    },
    2: {
        next: analyticsService.onboardingPressedNextCreatePassword,
        back: analyticsService.onboardingPressedBackCreatePassword,
    },
    3: {
        next: analyticsService.onboardingPressedBackPersonalDetails,
        back: analyticsService.onboardingPressedNextPersonalDetails,
    },
}

const useRegisterScreen = () => {
    const [activeStep, setActiveStep] = useState(0)
    const [generalError, setGeneralError] = useState(null)
    const [loginData, setLoginData] = useState(null)
    const [validationError, setValidationError] = useState(null)

    const [doVerifyOrgCode] = useVerifyOrgCodeMutation()

    const methods = useForm({
        mode: 'all',
        resolver: yupResolver(registerSchema),
    })

    const {watch, handleSubmit} = methods
    const values = watch()

    const onSubmit = async (submittedValues) => {
        setGeneralError(null)
        clearSignupError()
        clearVerifySmsError()
        await doSignup(submittedValues)
        setLoginData(submittedValues)
    }

    const {
        doVerifySms,
        doSendSms,
        isLoading: isLoadingVerifySms,
        verifySmsError,
        sendSmsError,
        signupVerified,
        clearVerifySmsError,
        smsSent,
    } = useVerifySms(true)

    const {doSignup, signupError, signupResponse, clearSignupError} =
        useSignup()

    const {doLogin, loginResponse} = useLogin()

    useEffect(() => {
        if (loginResponse) {
            window.location.reload()
        }
    }, [loginResponse])

    useEffect(() => {
        if (signupError) {
            setGeneralError(signupError?.data || signupError)
        }
    }, [signupError])

    const handleSendSms = async () => {
        console.log('values', values)
        let res = {success: true}
        if (values.PlannerSubscriptions) {
            const response = await doVerifyOrgCode({
                orgCode: values.PlannerSubscriptions,
            })

            res = response?.data || response?.error
        }
        if (res?.success) {
            doSendSms({
                phone: values.username,
            })
            return
        }
        alert(res?.msg || res?.data || 'שגיאה בקוד ארגון')
    }

    useEffect(() => {
        if (smsSent) {
            goNext()
        }
    }, [smsSent])

    useEffect(() => {
        if (sendSmsError) {
            setGeneralError(sendSmsError)
        }
    }, [sendSmsError])

    const handleVerifySms = () => {
        doVerifySms({
            phone: values.username,
            sms: values.sms,
        })
    }

    useEffect(() => {
        if (signupVerified) {
            goNext()
        }
    }, [signupVerified])

    useEffect(() => {
        if (signupResponse) {
            analyticsService.onboardingCreateAccount()
        }
    }, [signupResponse])

    useEffect(() => {
        if (verifySmsError) {
            setGeneralError(verifySmsError)
        }
    }, [verifySmsError])

    const goNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleNext = async () => {
        setGeneralError(null)
        analyticsFunctions[activeStep]?.next()
        switch (activeStep) {
            case 0:
                handleSendSms()
                break
            case 1:
                handleVerifySms()
                break
            case 3:
                break
            default:
                goNext()
        }
    }

    const handleBack = () => {
        setGeneralError(null)
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
        analyticsFunctions[activeStep]?.back()
    }

    const onDone = () => {
        const ulth = signupResponse?.msg
        analyticsService.onboardingPressedLogin()
        doLogin(loginData, ulth)
    }

    const registerSteps = useMemo(
        () => Object.keys(registerStepsComponents),
        []
    )

    const buttonText = useMemo(() => {
        return activeStep === registerSteps.length - 1 ? 'finish' : 'next'
    }, [activeStep, registerSteps.length])

    const register = handleSubmit(onSubmit)

    return {
        register,
        onDone,
        handleBack,
        registerSteps,
        handleNext,
        isLoadingVerifySms,
        buttonText,
        activeStep,
        signupResponse,
        methods,
        generalError,
        validationError,
        setValidationError,
    }
}

export default useRegisterScreen
